import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

export type TeamWithMembers = Tables<"teams"> & {
  team_members?: (Tables<"team_members"> & {
    user?: Tables<"users">;
  })[];
  leader?: Tables<"users"> | null;
};

async function fetchTeams() {
  const { data, error } = await supabase
    .from("teams")
    .select("*, leader:users(*)")
    .order("name");

  if (error) throw error;
  return data as TeamWithMembers[];
}

async function fetchTeamById(id: string) {
  const { data, error } = await supabase
    .from("teams")
    .select(
      `
      *,
      leader:users(*),
      team_members(*, user:users(*))
    `,
    )
    .eq("id", id)
    .single();

  if (error) throw error;
  return data as TeamWithMembers;
}

async function createTeam(team: InsertTables<"teams">) {
  const { data, error } = await supabase
    .from("teams")
    .insert([team])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateTeam(id: string, updates: UpdateTables<"teams">) {
  const { data, error } = await supabase
    .from("teams")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteTeam(id: string) {
  const { error } = await supabase.from("teams").delete().eq("id", id);

  if (error) throw error;
}

export function useTeams() {
  return useQuery({
    queryKey: ["teams"],
    queryFn: fetchTeams,
  });
}

export function useTeam(id: string) {
  return useQuery({
    queryKey: ["team", id],
    queryFn: () => fetchTeamById(id),
    enabled: !!id,
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"teams">;
    }) => updateTeam(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["team", id] });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}
